import {
    ActionField,
    ActionPlanAction,
    DataListOption,
    FieldValue,
} from "src/api/client-api/ActionPlan/ActionPlanAction";
import { GetOnePost } from "src/api/client-api/post/useGetOnePost";
import { getActionFieldByMultiElement } from "./getActionFieldByMultiElement";
import { getRowHierarchy } from "./getRowHierarchy";
import { getSubpropertiesFromPropertyTree } from "./getSubpropertiesFromPropertyTree";
import { ScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

type Options = {
    startingPost: GetOnePost | undefined;

    // Current ActionPlanAction
    actionPlanAction: ActionPlanAction;

    // Used to know which Subproperty will be used
    actionFields: ActionField[];

    // Actual Elements retrieved in a separate request
    fieldValues: FieldValue[] | undefined;

    // Callback for translations
    getDefaultLabel: (pos: number | undefined) => string;
};
export const formatMultivalue = (options: Options): ActionMultivalue | null => {
    const { actionFields, actionPlanAction, fieldValues, getDefaultLabel, startingPost } = options;

    // const maxMultivaluePosition = getMaxMultivaluePosition(actionPlanAction, actionFields);
    const { pivot, properties } = getSubpropertiesFromPropertyTree(actionPlanAction);
    // const [pivotSubproperty, ...otherSubproperties] = subproperties;

    if (pivot === null) {
        console.error("You need a Pivot Subproperty.");
        return null;
    }

    // BEFORE: maxMultivaluePosition = Supposedly all good items
    // Now: I need to match between Previous Items => New Items;
    const allPropertyIds = properties.reduce((acc, i) => acc.add(i.id), new Set<number>());
    const _allExistingPositions = actionPlanAction.propertiesTreeElements.reduce((acc, elem) => {
        if (allPropertyIds.has(elem.ssq_property_id)) {
            return acc.add(elem.position);
        }
        return acc;
    }, new Set<number>());
    const allExistingPositions = [..._allExistingPositions.keys()];

    const rowItems = allExistingPositions.map((actualPosition) => {
        const actionFieldByMulti = getActionFieldByMultiElement({
            position: actualPosition,
            actionFields,
            actionPlanAction,
            subproperties: properties,
            fieldValues,
            startingPost,
        });

        const rowHierarchy = getRowHierarchy({
            actionFields,
            actionPlanAction,
            getDefaultLabel,
            pivotSubproperty: pivot,
            otherSubproperties: properties,
            rowPosition: actualPosition,
        });
        const multirow: ActionMultiRow = {
            position: actualPosition,
            // Since i have row hierarchy here, i can build the meta data block.
            rowHierarchy,
            actionFieldByMulti,
            multiByAction: {
                totalImpact: 0,
                data: [],
            },
        };
        return multirow;
    });

    return {
        rows: rowItems,
    };
};

export type ActionSubproperty = {
    disabled: boolean;
    hidden: boolean;
    question: string;
    unit: string | undefined;
    fieldType: string;
    fieldValue: FieldValue | undefined;
    dataListOptions: DataListOption[] | undefined;
    ssqPropertyId: number | undefined;
    position: number;
    actionFieldId: number;
    startingValue: {
        label: string | undefined;
        value: string | number | undefined;
        unit: string | undefined;
    };
};
export type RowHierarchy = {
    // For multivalue, it is...
    // The actual value for pivot subproperty
    label: string;
    // The pivot subproperty name
    title: string;

    // The pivot subproperty PARENT name.
    parent: string | null;
};

// This may be PASTED from ActionSubproperty ? :)
export type ActionMultiProperty = {
    title: string;
    impact: ScoreValue;

    // With this, we're able to make either a difference on the UI
    // Or decide to just show, etc...
    previousValue: string;
    afterValue: string;
};

export type MultiByAction = {
    actionId: number;
    actionName: string;
    multiElements: ActionMultiProperty[];
};

export type ActionMultiRow = {
    position: number;
    rowHierarchy: RowHierarchy;
    actionFieldByMulti: ActionSubproperty[];
    multiByAction: {
        totalImpact: ScoreValue;
        data: MultiByAction[];
    };
};
export type ActionMultivalue = {
    rows: ActionMultiRow[];
};
